import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { getScheduleById, editSchedule, sendQuoteToCaseManager, getJobScheduleAuditLog, previewScheduleRegeneration } from '../services/scheduleService';
import { getJobsByScheduleId, updateJobStatus } from '../services/jobService';
import { searchFranchisees } from '../services/franchiseeService';
import { getClientById } from '../services/clientService';
import { listServiceTypes } from '../services/serviceTypeService';
import { editHousehold, getHouseholdById } from '../services/householdService';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { debounce, isEqual } from 'lodash';
import { FaMapMarkerAlt, FaCheck } from 'react-icons/fa';
import { terminology, allocationTypes, scheduleTypes, frequencyIntervalUnits, jobStatuses, quoteStatuses, formatters, allocationIntervalUnits } from '../config/terminology';
import ConfirmationModal from '../components/ConfirmationModal';
import RegenerateJobsWarningModal from '../components/RegenerateJobsWarningModal';
import ActivityLog from '../components/ActivityLog';
import { isCarwashService } from '../utils/carwashUtils';

// Mirrors hasDateAffectingChanges in the API's jobScheduleService — the fields that trigger
// delete-and-regenerate. Kept in sync deliberately; the counts shown come from the backend preview,
// so drift here changes only whether we warn, never what actually gets deleted.
const DATE_AFFECTING_FIELDS = [
    'scheduleType', 'frequencyType', 'frequencyValue',
    'startDate', 'endDate', 'occurrences', 'jobDate', 'jobTime',
];
const hasDateAffectingChanges = (payload) =>
    DATE_AFFECTING_FIELDS.some(field => Object.prototype.hasOwnProperty.call(payload, field));

// Helper to format Firestore Timestamp for date input
const formatTimestampForInput = (timestamp) => {
    // Check for a valid timestamp object and the presence of the 'seconds' or '_seconds' property
    const seconds = timestamp?.seconds ?? timestamp?._seconds;
    if (typeof seconds !== 'number') return '';
    // Use timestamp.seconds instead of timestamp._seconds
    const date = new Date(seconds * 1000);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD
};

// Re-use useSearch hook (consider moving to a shared hooks file later)
const useSearch = (searchFunction, options = {}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const optionsString = JSON.stringify(options);

    const debouncedFetch = useCallback(debounce(async (term, fetchOptions) => {
        if (!searchFunction || term.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        setIsSearching(true);
        try {
            const data = await searchFunction({ searchString: term, ...fetchOptions });
            const results = data.franchisees || [];
            setSuggestions(results);
            setShowSuggestions(true);
        } catch (error) {
            console.error("Search error:", error);
            setSuggestions([]);
            setShowSuggestions(false);
        } finally {
            setIsSearching(false);
        }
    }, 300), [searchFunction]);

    useEffect(() => {
        const currentOptions = JSON.parse(optionsString);
        debouncedFetch(searchTerm, currentOptions);
        return () => debouncedFetch.cancel();
    }, [searchTerm, optionsString, debouncedFetch]);

    return { searchTerm, setSearchTerm, suggestions, setSuggestions, isSearching, showSuggestions, setShowSuggestions };
};


const EditJobSchedulePage = () => {
    const { id: scheduleId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [apiError, setApiError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [originalScheduleData, setOriginalScheduleData] = useState(null);
    const [initialStatus, setInitialStatus] = useState(null);
    const [latestHouseholdInfo, setLatestHouseholdInfo] = useState(null);
    const [auditLog, setAuditLog] = useState([]);
    const [auditLogLoading, setAuditLogLoading] = useState(false);
    const [auditLogExpanded, setAuditLogExpanded] = useState(false);

    const [allServiceTypes, setAllServiceTypes] = useState([]);
    const [serviceTypesLoading, setServiceTypesLoading] = useState(true);
    const [selectedServiceIds, setSelectedServiceIds] = useState([]);
    const [serviceSelectionError, setServiceSelectionError] = useState(null);

    // Add new state for quote status updates

    // Add state for quote status modal
    const [isQuoteStatusModalOpen, setIsQuoteStatusModalOpen] = useState(false);
    const [pendingQuoteStatus, setPendingQuoteStatus] = useState(null);

    // Add state for status change confirmation modal during save
    const [isStatusChangeModalOpen, setIsStatusChangeModalOpen] = useState(false);
    const [pendingSaveData, setPendingSaveData] = useState(null);

    // Confirmation before a date change deletes and regenerates this schedule's jobs
    const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
    const [regenPreview, setRegenPreview] = useState(null);
    const [regenPreviewLoading, setRegenPreviewLoading] = useState(false);
    const [regenPreviewError, setRegenPreviewError] = useState(null);
    const [regenNewStartDate, setRegenNewStartDate] = useState(null);
    
    // Add simple change tracking
    const [hasChanges, setHasChanges] = useState(false);
    
    // Add success message state
    const [successMessage, setSuccessMessage] = useState(null);
    // Add state for case manager email functionality
    const [sendingToCaseManager, setSendingToCaseManager] = useState(false);
    const [emailSentToCaseManager, setEmailSentToCaseManager] = useState(false);

    // Add state for case manager editing
    const [caseManagerEmail, setCaseManagerEmail] = useState('');
    const [caseManagerName, setCaseManagerName] = useState('');
    const [selectedSupplier, setSelectedSupplier] = useState(null);

    // Fetch the latest household info whenever the household ID changes
    useEffect(() => {
        const fetchLatestHouseholdInfo = async () => {
            if (!originalScheduleData?.householdId) {
                return;
            }

            try {
                const householdData = await getHouseholdById(originalScheduleData.householdId);
                if (householdData) {
                    setLatestHouseholdInfo(householdData);

                    // Update case manager info when household info changes
                    const updatedEmail = householdData?.siteContact?.email || '';
                    const updatedName = householdData?.siteContact?.name || '';
                    setCaseManagerEmail(updatedEmail);
                    setCaseManagerName(updatedName);
                }
            } catch (error) {
                console.error('Error fetching latest household info:', error);
            }
        };

        fetchLatestHouseholdInfo();
    }, [originalScheduleData?.householdId]);

    const { register, handleSubmit, watch, setValue, reset, formState: { errors, isSubmitting, dirtyFields }, getValues} = useForm({
        mode: 'onBlur',
        defaultValues: {
            fseId: '',
            fseNameSearch: '',
            clientId: '',
            clientName: '',
            householdId: '',
            householdName: '',
            householdAddress: '',
            scheduleType: '',
            startDate: '',
            endDate: null,
            occurrences: null,
            jobTime: null,
            jobDate: '',
            frequencyOption: 'interval',
            frequencyIntervalValue: 7,
            frequencyIntervalUnit: 'Days',
            frequencyType: '',
            frequencyValue: null,
            allocationType: 'fixed-fee',
            allocationOption: 'interval',
            allocationIntervalValue: 7,
            allocationIntervalUnit: 'Days',
            allotedHours: null,
            pricePerHour: null,
            additionalPrice: null,
            invoicePricePerHour: null,
            description: null,            adminNotes: null,
            notesForCaseManager: null,
            notesForFranchisee: null,
            notesFromFranchisee: null,
            status: 'active',
            quoteStatus: '',
            ndisJob: false,
        }
    });
    const scheduleType = watch('scheduleType');
    const frequencyOption = watch('frequencyOption');
    const frequencyIntervalValue = watch('frequencyIntervalValue');
    const frequencyIntervalUnit = watch('frequencyIntervalUnit');
    const allocationType = watch('allocationType');
    const allocationOption = watch('allocationOption');
    const quoteStatus = watch('quoteStatus');
    const currentStatus = watch('status');
    const ndisJob = watch('ndisJob');
    const clientId = watch('clientId');
    const invoicePricePerHour = watch('invoicePricePerHour');
    const pricePerHour = watch('pricePerHour');

    // Fetch supplier data when clientId is available
    useEffect(() => {
        const fetchSupplierData = async () => {
            if (clientId) {
                try {
                    const supplierData = await getClientById(clientId);
                    setSelectedSupplier(supplierData);
                } catch (error) {
                    console.error("Failed to fetch supplier data:", error);
                    setSelectedSupplier(null);
                }
            } else {
                setSelectedSupplier(null);
            }
        };
        fetchSupplierData();
    }, [clientId]);

    // Auto-update description and invoice prices when FSE price changes and supplier has hourly rate
    useEffect(() => {
        if (pricePerHour && selectedSupplier?.hourlyRate) {
            const fsePrice = parseFloat(pricePerHour);
            const hourlyRate = parseFloat(selectedSupplier.hourlyRate);

            if (!isNaN(fsePrice) && !isNaN(hourlyRate) && hourlyRate > 0) {
                // Use the raw FSE price for duration calculation
                let duration = fsePrice / hourlyRate;

                // Round up to 15-minute increments (0.25 hours) if durationInIncrements is enabled
                if (selectedSupplier?.durationInIncrements) {
                    duration = Math.ceil(duration * 4) / 4;
                }

                const descriptionText = `Duration ${duration.toFixed(2)} hours based on hourly rate of $${hourlyRate.toFixed(2)} + GST.`;
                setValue('description', descriptionText, { shouldValidate: false, shouldDirty: true });

                // Calculate new Invoice Price (ex. GST) based on the calculated duration
                const invoicePriceExGST = duration * hourlyRate;
                setValue('additionalPrice', invoicePriceExGST.toFixed(2), { shouldValidate: true, shouldDirty: true });

                // **NEW: Save the duration directly into allotedHours so it maps to the invoice quantity later**
                setValue('allotedHours', duration, { shouldValidate: true, shouldDirty: true });
            }
        }
    }, [pricePerHour, selectedSupplier, ndisJob, setValue]);

    useEffect(() => {
        const fetchServices = async () => {
            setServiceTypesLoading(true);
            try {
                const services = await listServiceTypes();
                setAllServiceTypes(services);
            } catch (error) {
                console.error("Failed to fetch service types:", error);
                setApiError("Could not load service types. Please try again later.");
            } finally {
                setServiceTypesLoading(false);
            }
        };
        fetchServices();
    }, []);

    const fetchAuditLog = useCallback(async () => {
        if (!scheduleId) return;
        setAuditLogLoading(true);
        try {
            setAuditLog(await getJobScheduleAuditLog(scheduleId));
        } catch (err) {
            console.error('[EditJobSchedulePage] Failed to fetch activity log:', err);
            setAuditLog([]);
        } finally {
            setAuditLogLoading(false);
        }
    }, [scheduleId]);

    useEffect(() => {
        fetchAuditLog();
    }, [fetchAuditLog]);

    useEffect(() => {
        const fetchSchedule = async () => {
            setLoading(true);
            setApiError(null);
            try {
                const data = await getScheduleById(scheduleId);
                console.log("Fetched schedule details:", data); // Log the fetched data
                setOriginalScheduleData(data);
                setInitialStatus(data.status || 'active');

                const fetchedFrequencyTypeLower = data.frequencyType?.toLowerCase();
                let intervalUnit = 'Days';
                if (fetchedFrequencyTypeLower === 'days' || fetchedFrequencyTypeLower === 'weeks') {
                    intervalUnit = data.frequencyType.charAt(0).toUpperCase() + data.frequencyType.slice(1);
                }

                // Reverse transformation for allocation fields (backend → UI)
                let allocationTypeUI = '';
                let allocationOptionValue = 'interval';
                let allocationIntervalValueUI = 7;
                let allocationIntervalUnitUI = 'Days';
                
                if (data.allocationType === 'set-fee' || data.allocationType === 'fixed-fee') {
                    allocationTypeUI = 'fixed-fee';
                } else if (data.allocationType === 'fixed-hours') {
                    allocationTypeUI = 'fixed-hours';
                } else if (data.allocationType === 'monthly') {
                    allocationTypeUI = 'fixed-hours';
                    allocationOptionValue = 'monthly';
                    allocationIntervalValueUI = data.allocationValue || 7;
                    allocationIntervalUnitUI = 'Days';
                } else if (data.allocationType === 'days' || data.allocationType === 'weeks') {
                    allocationTypeUI = 'fixed-hours';
                    allocationOptionValue = 'interval';
                    allocationIntervalValueUI = data.allocationValue || 7;
                    allocationIntervalUnitUI = data.allocationType.charAt(0).toUpperCase() + data.allocationType.slice(1);
                }

                const formData = {
                    fseId: data.fseId || '',
                    fseNameSearch: `${data.franchiseeInfo?.franchiseName || ''}${data.franchiseeInfo?.firstName || data.franchiseeInfo?.lastName ? ` - ${data.franchiseeInfo?.firstName || ''} ${data.franchiseeInfo?.lastName || ''}`.trim() : ''}`.trim(),
                    clientId: data.clientId || '',
                    clientName: data.clientInfo?.clientName || 'N/A',
                    householdId: data.householdId || '',
                    householdName: `${data.householdInfo?.firstName || ''} ${data.householdInfo?.lastName || ''}`.trim() || 'N/A',
                    householdAddress: `${data.householdInfo?.addressLine1 || ''}${data.householdInfo?.addressLine2 ? ', ' + data.householdInfo.addressLine2 : ''}, ${data.householdInfo?.state || ''} ${data.householdInfo?.postalCode || ''}`.replace(/^, /,'').replace(/, $/,'').trim() || 'No address',
                    scheduleType: data.scheduleType || '',
                    startDate: formatTimestampForInput(data.startDate),
                    endDate: data.endDate ? formatTimestampForInput(data.endDate) : null,
                    occurrences: data.occurrences ?? null,
                    jobTime: data.jobTime || null,
                    jobDate: data.scheduleType === 'one-off' ? formatTimestampForInput(data.jobDate || data.startDate) : '',
                    frequencyType: data.frequencyType || '',
                    frequencyValue: data.frequencyValue ?? null,
                    allocationType: allocationTypeUI,
                    allotedHours: data.allotedHours ?? null,
                    pricePerHour: data.pricePerHour ?? null,
                    additionalPrice: data.additionalPrice ?? null,
                    invoicePricePerHour: data.invoicePricePerHour ?? null,
                    description: data.description || null,
                    adminNotes: data.adminNotes || null,
                    notesForCaseManager: data.notesForCaseManager || null,
                    notesForFranchisee: data.notesForFranchisee || null,
                    notesFromFranchisee: data.notesFromFranchisee || null,
                    status: data.status || 'active',
                    quoteStatus: data.quoteStatus || '',
                    frequencyOption: fetchedFrequencyTypeLower === 'monthly' ? 'monthly' : 'interval',                    frequencyIntervalValue: fetchedFrequencyTypeLower !== 'monthly' ? (data.frequencyValue || 7) : 7,
                    frequencyIntervalUnit: intervalUnit,
                    // UI allocation fields
                    allocationOption: allocationOptionValue,
                    allocationIntervalValue: allocationIntervalValueUI,
                    allocationIntervalUnit: allocationIntervalUnitUI,
                    servicesDirty: false,
                    ndisJob: data.ndisJob || false, // Include NDIS Job flag
                };
                reset(formData);

                const serviceIds = data.services?.map(s => s.serviceTypeId) || [];
                setSelectedServiceIds(serviceIds);

                // Check if quote is already pending approval and set email tracking state
                if (data.quoteStatus === 'pending-approval') {
                    setEmailSentToCaseManager(true);
                }

                // Initialize case manager info from household data
                const initialEmail = data.householdInfo?.siteContact?.email || '';
                const initialName = data.householdInfo?.siteContact?.name || '';
                setCaseManagerEmail(initialEmail);
                setCaseManagerName(initialName);

            } catch (error) {
                setApiError(error.message || `Failed to load schedule details.`);
                console.error("Fetch error:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchSchedule();
    }, [scheduleId, reset]);

    const additionalPrice = watch('additionalPrice');

    useEffect(() => {
        const hasAdditionalPriceValue =
            additionalPrice !== null &&
            additionalPrice !== '' &&
            !isNaN(parseFloat(additionalPrice)) &&
            parseFloat(additionalPrice) >= 0;

        if (hasAdditionalPriceValue) {
            const invoicePriceExGST = parseFloat(additionalPrice);
            // Don't add GST for NDIS jobs
            const invoicePriceWithGST = ndisJob
                ? invoicePriceExGST.toFixed(2)
                : (invoicePriceExGST * 1.1).toFixed(2);
            setValue('invoicePricePerHour', invoicePriceWithGST, { shouldValidate: true, shouldDirty: true });
        } else if (dirtyFields.additionalPrice) {
            setValue('invoicePricePerHour', null, { shouldValidate: false, shouldDirty: true });
        }
    }, [additionalPrice, ndisJob, setValue, dirtyFields.additionalPrice]);

    // Sync allocation fields with frequency fields for Fixed Hours allocation type (only for recurring schedules)
    useEffect(() => {
        if (allocationType === 'fixed-hours' && scheduleType === 'recurring') {
            setValue('allocationOption', frequencyOption, { shouldValidate: false });
            setValue('allocationIntervalValue', frequencyIntervalValue, { shouldValidate: false });
            setValue('allocationIntervalUnit', frequencyIntervalUnit, { shouldValidate: false });
        }
    }, [allocationType, scheduleType, frequencyOption, frequencyIntervalValue, frequencyIntervalUnit, setValue]);

    // Monitor form changes and update hasChanges state
    useEffect(() => {
        if (originalScheduleData && allServiceTypes.length > 0) {
            const hasFormChanges = checkForChanges();
            setHasChanges(hasFormChanges);
        }    }, [
        watch('fseId'), watch('scheduleType'), watch('startDate'), watch('endDate'),
        watch('occurrences'), watch('jobTime'), watch('allocationType'), watch('allotedHours'),
        watch('pricePerHour'), watch('invoicePricePerHour'), watch('description'),
        watch('adminNotes'), watch('notesForCaseManager'), watch('notesForFranchisee'), watch('notesFromFranchisee'), watch('status'), watch('quoteStatus'),
        watch('frequencyOption'), watch('frequencyIntervalValue'), watch('frequencyIntervalUnit'),
        watch('allocationOption'), watch('allocationIntervalValue'), watch('allocationIntervalUnit'),
        watch('ndisJob'), // Add NDIS Job to watched fields
        caseManagerName, caseManagerEmail, // Add case manager fields to watched fields
        selectedServiceIds, originalScheduleData, allServiceTypes
    ]);

    const franchiseeSearch = useSearch(searchFranchisees);

    const fseHandlers = {
        onChange: (e) => {
            const value = e.target.value;
            franchiseeSearch.setSearchTerm(value);
            setValue('fseNameSearch', value, { shouldDirty: true });
            setValue('fseId', '', { shouldDirty: true });
            if (value.length < 3) franchiseeSearch.setShowSuggestions(false);
        },
        onSelect: (item) => {
            setValue('fseId', item.id, { shouldDirty: true });
            const contactName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
            const displayName = `${item.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
            setValue('fseNameSearch', displayName, { shouldDirty: true });
            franchiseeSearch.setSuggestions([]);
            franchiseeSearch.setShowSuggestions(false);
        },
        renderSuggestions: (item) => {
            const contactName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
            return `${item.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
        }
    };


    const handleServiceToggle = (serviceId) => {
        setSelectedServiceIds(prev => {
            const isCurrentlySelected = prev.includes(serviceId);
            const isCarwash = isCarwashService(serviceId, allServiceTypes);

            // If deselecting the service
            if (isCurrentlySelected) {
                return prev.filter(id => id !== serviceId);
            }

            // If selecting a carwash service
            if (isCarwash) {
                // Clear all other selections and only select carwash
                return [serviceId];
            }

            // If selecting a non-carwash service
            // Check if any currently selected services are carwash
            const hasCarwashSelected = prev.some(id => isCarwashService(id, allServiceTypes));

            if (hasCarwashSelected) {
                // Replace carwash selection with this new service
                return [serviceId];
            } else {
                // Normal multi-selection for non-carwash services
                return [...prev, serviceId];
            }
        });
        setServiceSelectionError(null);
        setHasChanges(true); // Mark that changes have been made
    };

    // Helper function to detect if there are any changes
    const checkForChanges = () => {
        if (!originalScheduleData) return false;

        const currentFormData = watch();
        
        // Transform current form data the same way as in onSubmit
        let finalAllocationType = currentFormData.allocationType;
        let finalAllocationValue = null;

        if (currentFormData.allocationType === 'fixed-fee') {
            finalAllocationType = 'fixed-fee';
            finalAllocationValue = null;
        } else if (currentFormData.allocationType === 'fixed-hours') {
            if (currentFormData.allocationOption === 'monthly') {
                finalAllocationType = 'monthly';
                finalAllocationValue = null;
            } else {
                finalAllocationType = currentFormData.allocationIntervalUnit?.toLowerCase();
                finalAllocationValue = parseInt(currentFormData.allocationIntervalValue, 10);
            }
        }

        const serviceNamesMap = new Map(allServiceTypes.map(service => [service.id, service.name]));
        const servicesArray = selectedServiceIds.map(id => ({ serviceTypeId: id, name: serviceNamesMap.get(id) })).filter(s => s.name);

        let currentData = {
            fseId: currentFormData.fseId,
            services: servicesArray,
            scheduleType: currentFormData.scheduleType,
            startDate: currentFormData.startDate,
            endDate: currentFormData.endDate || null,
            occurrences: currentFormData.occurrences ? parseInt(currentFormData.occurrences, 10) : null,
            jobTime: currentFormData.jobTime || null,
            allocationType: finalAllocationType,
            allocationValue: finalAllocationValue,
            allotedHours: currentFormData.allotedHours ? parseFloat(currentFormData.allotedHours) : null,
            pricePerHour: currentFormData.pricePerHour ? parseFloat(currentFormData.pricePerHour) : null,
            additionalPrice: currentFormData.additionalPrice ?? null,
            invoicePricePerHour: currentFormData.invoicePricePerHour ? parseFloat(currentFormData.invoicePricePerHour) : null,
            description: currentFormData.description || null,
            adminNotes: currentFormData.adminNotes || null,            notesForFranchisee: currentFormData.notesForFranchisee || null,
            status: currentFormData.status,
            quoteStatus: currentFormData.quoteStatus,
            ndisJob: currentFormData.ndisJob || false, // Include NDIS Job flag
        };

        if (currentData.scheduleType === 'one-off') {
            currentData.jobDate = currentFormData.startDate;
            delete currentData.frequencyType;
            delete currentData.frequencyValue;
            delete currentData.endDate;
            delete currentData.occurrences;
        } else {
            delete currentData.jobDate;
            if (currentFormData.frequencyOption === 'monthly') {
                currentData.frequencyType = 'Monthly';
                currentData.frequencyValue = null;
            } else {
                currentData.frequencyType = currentFormData.frequencyIntervalUnit;
                currentData.frequencyValue = parseInt(currentFormData.frequencyIntervalValue, 10);
            }
        }

        // Clean up NaN values
        if (isNaN(currentData.allotedHours)) currentData.allotedHours = null;
        if (isNaN(currentData.pricePerHour)) currentData.pricePerHour = null;
        if (isNaN(currentData.additionalPrice)) currentData.additionalPrice = null;
        if (isNaN(currentData.invoicePricePerHour)) currentData.invoicePricePerHour = null;
        if (isNaN(currentData.occurrences)) currentData.occurrences = null;
        if (isNaN(currentData.frequencyValue)) currentData.frequencyValue = null;

        // Use the same comparison logic as onSubmit
        const looselyEqual = (val1, val2) => {
            const normVal1 = (val1 === undefined || val1 === '') ? null : val1;
            const normVal2 = (val2 === undefined || val2 === '') ? null : val2;
            if (typeof normVal1 === 'number' || typeof normVal2 === 'number') {
                if ((normVal1 === null && normVal2 !== null) || (normVal1 !== null && normVal2 === null)) return false;
                if (normVal1 === null && normVal2 === null) return true;
                return parseFloat(normVal1) === parseFloat(normVal2);
            }
            return normVal1 === normVal2;
        };        // Check simple fields
        const simpleFields = [
            'fseId', 'scheduleType', 'jobTime', 'allocationType',
            'description', 'adminNotes', 'notesForFranchisee', 'notesFromFranchisee', 'status', 'quoteStatus', 'ndisJob'
        ];
        for (const field of simpleFields) {
            if (!looselyEqual(currentData[field], originalScheduleData[field])) {
                return true;
            }
        }

        // Check numeric fields
        const numericFields = ['allotedHours', 'pricePerHour', 'additionalPrice', 'invoicePricePerHour', 'occurrences', 'frequencyValue', 'allocationValue'];
        for (const field of numericFields) {
            if (!looselyEqual(currentData[field], originalScheduleData[field])) {
                return true;
            }
        }

        // Check date fields
        const dateFields = { startDate: 'startDate', endDate: 'endDate', jobDate: 'jobDate' };
        for (const [formField, originalFieldKey] of Object.entries(dateFields)) {
            const currentValue = currentData[formField] || null;
            const originalValueString = originalScheduleData[originalFieldKey] ? formatTimestampForInput(originalScheduleData[originalFieldKey]) : null;
            if (currentValue !== originalValueString) {
                return true;
            }
        }

        // Check frequency type
        const originalFreqType = originalScheduleData.frequencyType || null;
        const currentFreqType = currentData.frequencyType || null;
        const originalFreqTypeNormalized = typeof originalFreqType === 'string' ? originalFreqType.toLowerCase() : null;
        const currentFreqTypeNormalized = typeof currentFreqType === 'string' ? currentFreqType.toLowerCase() : null;
        if (originalFreqTypeNormalized !== currentFreqTypeNormalized) {
            return true;
        }

        // Check services
        const originalServiceIds = (originalScheduleData.services || [])
            .map(s => s.serviceTypeId)
            .filter(Boolean)
            .sort();
        const currentServiceIds = selectedServiceIds.slice().sort();
        if (!isEqual(originalServiceIds, currentServiceIds)) {
            return true;
        }

        // Check case manager changes
        const originalCaseManagerName = originalScheduleData?.householdInfo?.siteContact?.name || '';
        const originalCaseManagerEmail = originalScheduleData?.householdInfo?.siteContact?.email || '';
        if (caseManagerName !== originalCaseManagerName || caseManagerEmail !== originalCaseManagerEmail) {
            return true;
        }

        return false;
    };    const performSave = async (payloadToSave) => {
        setApiError(null);
        
        // DEBUG: Log payload being saved
        console.log('🔍 EDIT JOB SCHEDULE - PERFORM SAVE DEBUGGING:');
        console.log('  Payload to save:', JSON.stringify(payloadToSave, null, 2));
        
        try {
            // Handle case manager updates if included in payload
            if (payloadToSave.caseManagerName !== undefined || payloadToSave.caseManagerEmail !== undefined) {
                const householdId = originalScheduleData?.householdId;
                if (householdId) {
                    const currentSiteContact = latestHouseholdInfo?.siteContact || {};
                    const updatedSiteContact = {
                        name: payloadToSave.caseManagerName !== undefined ? (payloadToSave.caseManagerName.trim() || null) : (currentSiteContact.name || null),
                        phone: currentSiteContact.phone || null,
                        email: payloadToSave.caseManagerEmail !== undefined ? (payloadToSave.caseManagerEmail.trim() || null) : (currentSiteContact.email || null)
                    };

                    // API call returns the updated household data
                    const updatedHouseholdData = await editHousehold(householdId, {
                        siteContact: updatedSiteContact
                    });

                    // Update local state with the returned data from API
                    setLatestHouseholdInfo(updatedHouseholdData);
                }
                // Note: caseManagerName and caseManagerEmail remain in payloadToSave
                // so the backend can update the schedule's denormalized householdInfo.siteContact
            }

            // DEBUG: Final payload sent to API
            console.log('  Final payload sent to editSchedule API:', JSON.stringify(payloadToSave, null, 2));

            const response = await editSchedule(scheduleId, payloadToSave);
            let successMsg = 'Job schedule details updated successfully!';
            if (response?.regenerationDetails) {
                successMsg += ` Future jobs regenerated (${response.regenerationDetails.deletedCount} deleted, ${response.regenerationDetails.generatedCount} generated).`;
            }

            // Auto-scroll to top for success feedback (before navigation)
            window.scrollTo({ top: 0, behavior: 'smooth' });

            const updatedOriginalData = { ...originalScheduleData, ...payloadToSave };

            if (payloadToSave.services) {
                updatedOriginalData.services = payloadToSave.services;
            } else if (originalScheduleData?.services) {
                updatedOriginalData.services = originalScheduleData.services;
            }
            setOriginalScheduleData(updatedOriginalData);
            fetchAuditLog();

            const fetchedFrequencyTypeLower = updatedOriginalData.frequencyType?.toLowerCase();
            let intervalUnit = 'Days';
            if (fetchedFrequencyTypeLower === 'days' || fetchedFrequencyTypeLower === 'weeks') {
                intervalUnit = updatedOriginalData.frequencyType.charAt(0).toUpperCase() + updatedOriginalData.frequencyType.slice(1);
            }

            // Reverse transformation for allocation fields (backend → UI)
            let allocationTypeUI = 'fixed-fee'; // Default UI allocation type
            let allocationOptionValue = 'interval'; // Default allocation option
            let allocationIntervalValueUI = 7;
            let allocationIntervalUnitUI = 'Days';
            
            if (updatedOriginalData.allocationType === 'set-fee' || updatedOriginalData.allocationType === 'fixed-fee') {
                allocationTypeUI = 'fixed-fee';
                allocationOptionValue = 'interval'; // Not used for fixed-fee, but set default
            } else if (updatedOriginalData.allocationType === 'fixed-hours') {
                allocationTypeUI = 'fixed-hours';
                allocationOptionValue = 'interval';
            } else if (updatedOriginalData.allocationType === 'monthly') {
                allocationTypeUI = 'fixed-hours';
                allocationOptionValue = 'monthly';
                allocationIntervalValueUI = updatedOriginalData.allocationValue || 7;
                allocationIntervalUnitUI = 'Days';
            } else if (updatedOriginalData.allocationType === 'days' || updatedOriginalData.allocationType === 'weeks') {
                allocationTypeUI = 'fixed-hours';
                allocationOptionValue = 'interval';
                allocationIntervalValueUI = updatedOriginalData.allocationValue || 7;
                allocationIntervalUnitUI = updatedOriginalData.allocationType.charAt(0).toUpperCase() + updatedOriginalData.allocationType.slice(1);
            }

            const formData = {
                fseId: updatedOriginalData.fseId || '',
                fseNameSearch: watch('fseNameSearch'),
                clientId: updatedOriginalData.clientId || '',
                clientName: updatedOriginalData.clientInfo?.clientName || 'N/A',
                householdId: updatedOriginalData.householdId || '',
                householdName: `${updatedOriginalData.householdInfo?.firstName || ''} ${updatedOriginalData.householdInfo?.lastName || ''}`.trim() || 'N/A',
                householdAddress: `${updatedOriginalData.householdInfo?.addressLine1 || ''}${updatedOriginalData.householdInfo?.addressLine2 ? ', ' + updatedOriginalData.householdInfo.addressLine2 : ''}, ${updatedOriginalData.householdInfo?.state || ''} ${updatedOriginalData.householdInfo?.postalCode || ''}`.replace(/^, /,'').replace(/, $/,'').trim() || 'No address',
                scheduleType: updatedOriginalData.scheduleType || '',
                startDate: updatedOriginalData.startDate ? (typeof updatedOriginalData.startDate === 'string' ? updatedOriginalData.startDate : formatTimestampForInput(updatedOriginalData.startDate)) : '',
                endDate: updatedOriginalData.endDate ? (typeof updatedOriginalData.endDate === 'string' ? updatedOriginalData.endDate : formatTimestampForInput(updatedOriginalData.endDate)) : null,
                occurrences: updatedOriginalData.occurrences ?? null,
                jobTime: updatedOriginalData.jobTime || null,
                jobDate: updatedOriginalData.scheduleType === 'one-off' ? (updatedOriginalData.jobDate ? (typeof updatedOriginalData.jobDate === 'string' ? updatedOriginalData.jobDate : formatTimestampForInput(updatedOriginalData.jobDate)) : '') : '',
                frequencyType: updatedOriginalData.frequencyType || '',
                frequencyValue: updatedOriginalData.frequencyValue ?? null,
                allocationType: allocationTypeUI, // Use the UI-transformed allocation type
                allotedHours: updatedOriginalData.allotedHours ?? null,
                pricePerHour: updatedOriginalData.pricePerHour ?? null,
                additionalPrice: updatedOriginalData.additionalPrice ?? null,
                invoicePricePerHour: updatedOriginalData.invoicePricePerHour ?? null,
                description: updatedOriginalData.description || null,
                adminNotes: updatedOriginalData.adminNotes || null,
                notesForCaseManager: updatedOriginalData.notesForCaseManager || null,
                notesForFranchisee: updatedOriginalData.notesForFranchisee || null,
                status: updatedOriginalData.status || 'active',
                quoteStatus: updatedOriginalData.quoteStatus || '',
                frequencyOption: fetchedFrequencyTypeLower === 'monthly' ? 'monthly' : 'interval',
                frequencyIntervalValue: fetchedFrequencyTypeLower !== 'monthly' ? (updatedOriginalData.frequencyValue || 7) : 7,
                frequencyIntervalUnit: intervalUnit,
                // UI allocation fields
                allocationOption: allocationOptionValue,
                allocationIntervalValue: allocationIntervalValueUI,
                allocationIntervalUnit: allocationIntervalUnitUI,
                servicesDirty: false,
            };
            reset(formData);

            const savedServiceIds = updatedOriginalData.services?.map(s => s.serviceTypeId) || [];
            setSelectedServiceIds(savedServiceIds);

            // Show success message without navigating away
            setApiError(null); // Clear any previous errors
            setSuccessMessage(successMsg); // Show success message
            setHasChanges(false); // Reset changes state since data has been saved
            if (payloadToSave.status) {
                setInitialStatus(payloadToSave.status);
            }
            
            // Reset email tracking state if quote status changed
            if (payloadToSave.quoteStatus && payloadToSave.quoteStatus !== 'pending-approval') {
                setEmailSentToCaseManager(false);
            }
            
            // Auto-hide success message after 5 seconds
            setTimeout(() => {
                setSuccessMessage(null);
            }, 5000);
            
        } catch (error) {
            // Auto-scroll to top to show error message
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setApiError(error.message || 'Failed to update job schedule details.');
            console.error("Update error:", error);
        }
    };

    const onSubmit = async (data) => {
        setApiError(null);
        setServiceSelectionError(null);
        setSuccessMessage(null); // Clear any previous success message

        // Auto-scroll to top to show any error messages
        window.scrollTo({ top: 0, behavior: 'smooth' });

        if (selectedServiceIds.length === 0 && currentStatus === 'active') {
            setServiceSelectionError("Please select at least one service for an active schedule.");
            return;
        }

        // Transform allocation fields for backend
        let finalAllocationType = data.allocationType;
        let finalAllocationValue = null;

        if (data.allocationType === 'fixed-fee') {
            // Keep as 'fixed-fee', no allocationValue needed
            finalAllocationType = 'fixed-fee';
            finalAllocationValue = null;
        } else if (data.allocationType === 'fixed-hours') {
            if (data.allocationOption === 'monthly') {
                finalAllocationType = 'monthly';
                finalAllocationValue = null;
            } else {
                finalAllocationType = data.allocationIntervalUnit.toLowerCase(); // 'days' or 'weeks'
                finalAllocationValue = parseInt(data.allocationIntervalValue, 10);
            }
        }

        const serviceNamesMap = new Map(allServiceTypes.map(service => [service.id, service.name]));
        const servicesArray = selectedServiceIds.map(id => ({ serviceTypeId: id, name: serviceNamesMap.get(id) })).filter(s => s.name);

        if (servicesArray.length !== selectedServiceIds.length && currentStatus === 'active') {
            setServiceSelectionError("Could not find names for all selected services. Please try again.");
            console.error("Failed to map service IDs to names:", selectedServiceIds, serviceNamesMap);
            return;
        }

        let currentData = {
            fseId: data.fseId,
            services: servicesArray,
            scheduleType: data.scheduleType,
            startDate: data.startDate,
            endDate: data.endDate || null,
            occurrences: data.occurrences ? parseInt(data.occurrences, 10) : null,
            jobTime: data.jobTime || null,
            allocationType: finalAllocationType,
            allocationValue: finalAllocationValue,
            allotedHours: data.allotedHours ? parseFloat(data.allotedHours) : null,
            pricePerHour: data.pricePerHour ? parseFloat(data.pricePerHour) : null,
            additionalPrice: data.additionalPrice ?? null,
            invoicePricePerHour: data.invoicePricePerHour ? parseFloat(data.invoicePricePerHour) : null,            description: data.description || null,
            adminNotes: data.adminNotes || null,            notesForFranchisee: data.notesForFranchisee || null,
            status: data.status,
            quoteStatus: data.quoteStatus,
            ndisJob: data.ndisJob || false, // Include NDIS Job flag
        };

        // DEBUG: Log NDIS job field tracking
        console.log('🔍 EDIT JOB SCHEDULE - NDIS DEBUGGING:');
        console.log('  Form data.ndisJob:', data.ndisJob);
        console.log('  Current data.ndisJob:', currentData.ndisJob);
        console.log('  Full currentData before processing:', JSON.stringify(currentData, null, 2));

        if (currentData.scheduleType === 'one-off') {
            currentData.jobDate = data.startDate; // Ensure jobDate is based on the form's startDate for one-off
            delete currentData.frequencyType;
            delete currentData.frequencyValue;
            delete currentData.endDate;
            delete currentData.occurrences;
        } else { // recurring
            delete currentData.jobDate;
            if (data.frequencyOption === 'monthly') {
                currentData.frequencyType = 'Monthly'; // Keep consistent casing, service might lowercase it
                currentData.frequencyValue = null; // Explicitly nullify
            } else {
                currentData.frequencyType = data.frequencyIntervalUnit;
                currentData.frequencyValue = parseInt(data.frequencyIntervalValue, 10);
            }
        }

        if (isNaN(currentData.allotedHours)) currentData.allotedHours = null;
        if (isNaN(currentData.pricePerHour)) currentData.pricePerHour = null;
        if (isNaN(currentData.additionalPrice)) currentData.additionalPrice = null;
        if (isNaN(currentData.invoicePricePerHour)) currentData.invoicePricePerHour = null;
        if (isNaN(currentData.occurrences)) currentData.occurrences = null;
        if (isNaN(currentData.frequencyValue)) currentData.frequencyValue = null;


        const updatePayload = {};
        if (!originalScheduleData) {
            setApiError("Original schedule data is missing. Cannot determine changes.");
            return;
        }

        const looselyEqual = (val1, val2) => {
            const normVal1 = (val1 === undefined || val1 === '') ? null : val1;
            const normVal2 = (val2 === undefined || val2 === '') ? null : val2;
            if (typeof normVal1 === 'number' || typeof normVal2 === 'number') {
                if ((normVal1 === null && normVal2 !== null) || (normVal1 !== null && normVal2 === null)) return false;
                if (normVal1 === null && normVal2 === null) return true;
                return parseFloat(normVal1) === parseFloat(normVal2);
            }
            return normVal1 === normVal2;
        };        const simpleFields = [
            'fseId', 'scheduleType', 'jobTime', 'allocationType',
            'description', 'adminNotes', 'notesForFranchisee', 'notesFromFranchisee', 'status', 'quoteStatus', 'ndisJob'
        ];
        simpleFields.forEach(field => {
            if (!looselyEqual(currentData[field], originalScheduleData[field])) {
                console.log(`📝 Field ${field} changed:`, {
                    current: currentData[field],
                    original: originalScheduleData[field],
                    equal: looselyEqual(currentData[field], originalScheduleData[field])
                });
                updatePayload[field] = currentData[field] ?? null;
            }
        });

        const numericFields = ['allotedHours', 'pricePerHour', 'additionalPrice', 'invoicePricePerHour', 'occurrences', 'frequencyValue', 'allocationValue'];
        numericFields.forEach(field => {
            if (!looselyEqual(currentData[field], originalScheduleData[field])) {
                updatePayload[field] = currentData[field] ?? null;
            }
        });

        const dateFields = { startDate: 'startDate', endDate: 'endDate', jobDate: 'jobDate' };
        Object.keys(dateFields).forEach(formField => {
            const originalFieldKey = dateFields[formField];
            // currentData[formField] should be a string from the form (e.g., data.startDate) or null
            const currentValue = currentData[formField] || null;
            const originalValueString = originalScheduleData[originalFieldKey] ? formatTimestampForInput(originalScheduleData[originalFieldKey]) : null;

            if (currentValue !== originalValueString) {
                 // currentValue should already be a string 'YYYY-MM-DD' or null from form processing
                updatePayload[formField] = currentValue;
            }
        });

        const originalFreqType = originalScheduleData.frequencyType || null;
        // currentData.frequencyType is already set correctly (e.g., 'Monthly', 'Days', 'Weeks')
        const currentFreqType = currentData.frequencyType || null;

        // Compare consistently (e.g. lowercase, though service might handle casing)
        const originalFreqTypeNormalized = typeof originalFreqType === 'string' ? originalFreqType.toLowerCase() : null;
        const currentFreqTypeNormalized = typeof currentFreqType === 'string' ? currentFreqType.toLowerCase() : null;

        if (originalFreqTypeNormalized !== currentFreqTypeNormalized) {
            updatePayload.frequencyType = currentFreqType; // Send with intended casing from currentData
        }


        const originalServiceIds = (originalScheduleData.services || [])
            .map(s => s.serviceTypeId)
            .filter(Boolean)
            .sort();
        const currentServiceIds = selectedServiceIds.slice().sort(); // selectedServiceIds are from state
        if (!isEqual(originalServiceIds, currentServiceIds)) {
            updatePayload.services = servicesArray;
        }

        // Handle cascading changes if scheduleType is modified
        if ('scheduleType' in updatePayload) {
            if (updatePayload.scheduleType === 'one-off') {
                // If switching to one-off, ensure recurring fields are nulled if not already handled
                if (originalScheduleData.frequencyType && !('frequencyType' in updatePayload)) updatePayload.frequencyType = null;
                if (originalScheduleData.frequencyValue && !('frequencyValue' in updatePayload)) updatePayload.frequencyValue = null;
                if (originalScheduleData.endDate && !('endDate' in updatePayload)) updatePayload.endDate = null;
                if (originalScheduleData.occurrences && !('occurrences' in updatePayload)) updatePayload.occurrences = null;
                // Ensure jobDate is set (it should be from currentData.jobDate = data.startDate)
                if (!('jobDate' in updatePayload) && currentData.jobDate) updatePayload.jobDate = currentData.jobDate;

            } else { // Switching to recurring
                if (originalScheduleData.jobDate && !('jobDate' in updatePayload)) updatePayload.jobDate = null;
                // Ensure frequencyType and potentially frequencyValue are set
                if (!('frequencyType' in updatePayload) && currentData.frequencyType) updatePayload.frequencyType = currentData.frequencyType;
                if (currentData.frequencyValue !== null && !('frequencyValue' in updatePayload) && currentData.frequencyType !== 'Monthly') {
                     updatePayload.frequencyValue = currentData.frequencyValue;
                }
                if (!('startDate' in updatePayload) && currentData.startDate) updatePayload.startDate = currentData.startDate;
            }
        }
        
        // Explicitly ensure date fields in updatePayload are strings 'YYYY-MM-DD'
        const dateKeysToEnsureString = ['startDate', 'endDate', 'jobDate'];
        dateKeysToEnsureString.forEach(key => {
            if (updatePayload.hasOwnProperty(key) && updatePayload[key] !== null) {
                if (typeof updatePayload[key] === 'object' && 
                    (updatePayload[key].seconds !== undefined || updatePayload[key]._seconds !== undefined)) {
                    // If it's a Timestamp-like object, format it
                    updatePayload[key] = formatTimestampForInput(updatePayload[key]);
                } else if (typeof updatePayload[key] !== 'string') {
                    // If it's some other non-string, non-null type (e.g. a Date object)
                    // This path is less likely if form inputs are standard type="date"
                    // but as a fallback, try to convert. formatTimestampForInput might return '' if not {seconds...}
                    // A more direct conversion for a JS Date object would be:
                    // updatePayload[key] = new Date(updatePayload[key]).toISOString().split('T')[0];
                    // For now, relying on formatTimestampForInput for {seconds...} objects primarily.
                    // If it's not a {seconds...} object and not a string, it's an anomaly.
                    // The most robust solution is to ensure form values (data.startDate etc.) are strings.
                }
            }
        });

        // Check for case manager changes
        const originalCaseManagerName = originalScheduleData?.householdInfo?.siteContact?.name || '';
        const originalCaseManagerEmail = originalScheduleData?.householdInfo?.siteContact?.email || '';

        if (caseManagerName !== originalCaseManagerName || caseManagerEmail !== originalCaseManagerEmail) {
            updatePayload.caseManagerName = caseManagerName;
            updatePayload.caseManagerEmail = caseManagerEmail;
        }

        // Check if any actual data changes are in updatePayload
        // The servicesDirty flag is handled by checking updatePayload.services
        console.log('🎯 Final updatePayload:', updatePayload);
        console.log('🎯 updatePayload keys:', Object.keys(updatePayload));
        
        if (Object.keys(updatePayload).length === 0) {
            setApiError("No changes detected to save.");
            return;
        }

        // Check if status is being changed and show confirmation modal
        if (updatePayload.status && (updatePayload.status === 'active' || updatePayload.status === 'inactive')) {
            setPendingSaveData(updatePayload);
            setIsStatusChangeModalOpen(true);
            return;
        }

        // A date-affecting change deletes every uncompleted job on the schedule and regenerates.
        // Jobs before the new start date never come back, so confirm with real numbers first.
        if (hasDateAffectingChanges(updatePayload)) {
            setPendingSaveData(updatePayload);
            openRegenerateWarning(updatePayload);
            return;
        }

        await performSave(updatePayload);
    };

    const openRegenerateWarning = async (payload) => {
        setRegenPreview(null);
        setRegenPreviewError(null);
        setRegenPreviewLoading(true);
        setIsRegenerateModalOpen(true);
        try {
            const proposedStart = payload.startDate || payload.jobDate || null;
            setRegenNewStartDate(proposedStart);
            setRegenPreview(await previewScheduleRegeneration(scheduleId, proposedStart));
        } catch (error) {
            // Advisory only — the modal still warns, it just cannot show counts.
            setRegenPreviewError(error.message || 'Unknown error');
        } finally {
            setRegenPreviewLoading(false);
        }
    };

    const closeRegenerateWarning = () => {
        setIsRegenerateModalOpen(false);
        setPendingSaveData(null);
        setRegenPreview(null);
        setRegenPreviewError(null);
        setRegenNewStartDate(null);
    };

    const handleConfirmRegenerate = async () => {
        const payload = pendingSaveData;
        setIsRegenerateModalOpen(false);
        setPendingSaveData(null);
        setRegenPreview(null);
        setRegenPreviewError(null);
        setRegenNewStartDate(null);
        if (payload) await performSave(payload);
    };

    // Update quote status handler to show confirmation first
    const handleQuoteStatusChange = (event) => {
        const newStatus = event.target.value;
        if (newStatus === watch('quoteStatus')) return;
        
        setPendingQuoteStatus(newStatus);
        setIsQuoteStatusModalOpen(true);
    };

    // Simple status change handler
    const handleStatusChange = (event) => {
        // React Hook Form handles this automatically through register
    };

    // Add handler for confirming quote status change
    const handleConfirmQuoteStatus = () => {
        setIsQuoteStatusModalOpen(false);
        setValue('quoteStatus', pendingQuoteStatus, { shouldDirty: true });
        setPendingQuoteStatus(null);
    };

    // Add handler for confirming status change during save
    const handleConfirmStatusChange = async () => {
        setIsStatusChangeModalOpen(false);
        if (pendingSaveData) {
            if (pendingSaveData.status === 'active') {
                const CANCELLABLE_STATUSES = ['pending-quote', 'approved', 'service-delayed', 'service-on-hold'];
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                try {
                    const jobs = await getJobsByScheduleId(scheduleId);
                    const pastJobsToCancel = jobs.filter(job => {
                        const seconds = job.jobDate?._seconds ?? job.jobDate?.seconds;
                        if (typeof seconds !== 'number') return false;
                        const jobDate = new Date(seconds * 1000);
                        return jobDate < today && CANCELLABLE_STATUSES.includes(job.status);
                    });

                    if (pastJobsToCancel.length > 0) {
                        await Promise.all(pastJobsToCancel.map(job => updateJobStatus(job.id, 'cancelled')));
                    }
                } catch (error) {
                    console.error('Error cancelling past jobs on activation:', error);
                    setApiError('Failed to cancel past jobs. Please try again.');
                    setPendingSaveData(null);
                    return;
                }
            }

            await performSave(pendingSaveData);
            setPendingSaveData(null);
        }
    };

    // Add handler for sending quote to case manager
    const handleSendToCaseManager = async () => {
        setSendingToCaseManager(true);
        setApiError(null);
        setSuccessMessage(null);
        const notesForCaseManager = getValues("notesForCaseManager");

        // Check if we have the latest household info
        if (!latestHouseholdInfo) {
            setApiError('Unable to fetch latest client information. Please try again.');
            setSendingToCaseManager(false);
            return;
        }

        try {
            // Pass the latest household info when sending to case manager
            const result = await sendQuoteToCaseManager(scheduleId, notesForCaseManager, latestHouseholdInfo);
            // Automatically update quote status to pending-approval
            setValue('quoteStatus', 'pending-approval', { shouldDirty: true });

            // Automatically save the form to persist the status change
            const updatePayload = {
                quoteStatus: 'pending-approval',
                notesForCaseManager,
            };

            // Save the updated status
            await performSave(updatePayload);

            setSuccessMessage(result.message || 'Quote sent to Case Manager successfully!');
            setEmailSentToCaseManager(true);

            // Auto-hide success message after 5 seconds
            setTimeout(() => {
                setSuccessMessage(null);
            }, 5000);
              } catch (error) {
            console.error("Error sending quote to case manager:", error);
            
            // Check if error is related to email/SMTP issues and show user-friendly message
            let errorMessage = 'Failed to send quote to Case Manager.';
            if (error.message && (
                error.message.includes('SMTP') || 
                error.message.includes('email') || 
                error.message.includes('Try again later') ||
                error.message.includes('server terminates connection') ||
                error.message.includes('gsmtp')
            )) {
                errorMessage = 'Unable to send email at the moment. Please try again later.';
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setApiError(errorMessage);
            
            // Auto-scroll to top to show error message
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setSendingToCaseManager(false);
        }
    };

    // Add handler for resending quote to case manager
    const handleResendToCaseManager = async () => {
        setSendingToCaseManager(true);
        setApiError(null);
        setSuccessMessage(null);

        try {
            const notesForCaseManager = getValues("notesForCaseManager");
            await sendQuoteToCaseManager(scheduleId, notesForCaseManager);
            setSuccessMessage('Quote resent to Case Manager successfully!');

            const updatePayload = {
                notesForCaseManager,
            };

            await performSave(updatePayload);

            // Auto-hide success message after 5 seconds
            setTimeout(() => {
                setSuccessMessage(null);
            }, 5000);
              } catch (error) {
            console.error("Error resending quote to case manager:", error);

            // Check if error is related to email/SMTP issues and show user-friendly message
            let errorMessage = 'Failed to resend quote to Case Manager.';
            if (error.message && (
                error.message.includes('SMTP') ||
                error.message.includes('email') ||
                error.message.includes('Try again later') ||
                error.message.includes('server terminates connection') ||
                error.message.includes('gsmtp')
            )) {
                errorMessage = 'Unable to send email at the moment. Please try again later.';
            } else if (error.message) {
                errorMessage = error.message;
            }

            setApiError(errorMessage);

            // Auto-scroll to top to show error message
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setSendingToCaseManager(false);
        }
    };





    if (loading) {
        return <p className="text-center text-gray-500 py-10">Loading schedule details...</p>;
    }

    // Extract site contact info from the latest household info or fall back to original data
    const siteContact = latestHouseholdInfo?.siteContact || originalScheduleData?.householdInfo?.siteContact || {};
    const siteContactName = siteContact.name || null;
    const siteContactPhone = siteContact.phone || null;

    console.log("siteContact",latestHouseholdInfo?.siteContact)

    return (
        <div className="container mx-auto px-4 pt-8 pb-20" style={{ maxWidth: '76rem' }}>
            <div className="flex justify-between items-center mb-2">
                <h1 className="text-3xl font-bold text-jims-cyan-dark">Edit Job Schedule</h1>
                <Link
                    to="/job-scheduler"
                    state={location.state?.filters ? { filters: location.state.filters } : {}}
                    className="text-jims-cyan-dark hover:text-jims-cyan underline"
                >
                    Back to Scheduler
                </Link>
            </div>

            {/* Transferred Schedule Indicator */}
            {originalScheduleData?.status === 'transferred' && originalScheduleData?.transferredToScheduleId && (
                <div className="mb-4 p-2 bg-yellow-100 text-yellow-800 border border-yellow-300 rounded text-sm">
                    This schedule was transferred to <strong>{originalScheduleData.transferredToFranchiseeInfo?.franchiseName || 'another franchisee'}</strong>.{' '}
                    <Link
                        to={`/job-scheduler/edit/${originalScheduleData.transferredToScheduleId}`}
                        className="underline font-semibold hover:text-yellow-900"
                    >
                        View new schedule →
                    </Link>
                </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-8 rounded-lg shadow-md space-y-6">
                {apiError && <p className="text-center text-red-600 bg-red-100 p-3 rounded mb-4">{apiError}</p>}
                {successMessage && <p className="text-center text-green-600 bg-green-100 p-3 rounded mb-4">{successMessage}</p>}

                <div className="relative">
                    <label htmlFor="fseNameSearch" className={`block text-sm font-medium text-gray-700`}>Franchisee *</label>
                    <input id="fseNameSearch" type="text" autoComplete="off"
                        {...register("fseNameSearch", { required: "Franchisee search is required." })}
                        value={watch('fseNameSearch')}
                        onChange={fseHandlers.onChange}
                        onBlur={() => setTimeout(() => franchiseeSearch.setShowSuggestions(false), 150)}
                        onFocus={() => franchiseeSearch.suggestions.length > 0 && franchiseeSearch.setShowSuggestions(true)}
                        className={`mt-1 block w-full input-field ${errors.fseNameSearch || (errors.fseId && !watch('fseId')) ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    <input type="hidden" {...register("fseId", { required: "Please select a franchisee." })} />
                    {errors.fseId && !watch('fseId') && <p className="input-error">{errors.fseId.message}</p>}
                    {franchiseeSearch.showSuggestions && (
                        <ul className="suggestions-list">
                            {franchiseeSearch.isSearching ? <li className="searching">Searching...</li> :
                             franchiseeSearch.suggestions.length > 0 ? franchiseeSearch.suggestions.map(item => (
                                <li key={item.id} className="suggestion-item" onMouseDown={() => fseHandlers.onSelect(item)}>
                                    {fseHandlers.renderSuggestions(item)}
                                </li>
                            )) : franchiseeSearch.searchTerm.length >= 3 && <li className="no-results">No franchisees found.</li>}
                        </ul>
                    )}

                   
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">{terminology.client.singular}</label>
                        <p className="mt-1 p-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-700">{watch('clientName')}</p>
                        <input type="hidden" {...register("clientId")} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">{terminology.household.singular}</label>
                        <p className="mt-1 p-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-700">{watch('householdName')}</p>
                        <p className="mt-1 text-xs text-gray-500">
                            <FaMapMarkerAlt className="inline mr-1"/> {watch('householdAddress')}
                        </p>
                        <input type="hidden" {...register("householdId")} />
                    </div>
                    
                </div>
             {/* Site Contact Info with editable name and email */}
                    <div className="mt-4 bg-gray-50 border border-gray-200 rounded p-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Case Manager Contact Details</h4>
                        <div className="flex gap-4 w-full">
                            {/* Case Manager Name */}
                            <div className="flex-1">
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Name</label>
                                <input
                                    type="text"
                                    value={caseManagerName}
                                    onChange={(e) => setCaseManagerName(e.target.value)}
                                    className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:border-jims-cyan transition duration-200"
                                    placeholder="Enter case manager name"
                                    disabled={isSubmitting}
                                />
                            </div>

                            {/* Case Manager Email */}
                            <div className="flex-1">
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                                <input
                                    type="email"
                                    value={caseManagerEmail}
                                    onChange={(e) => setCaseManagerEmail(e.target.value)}
                                    className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:border-jims-cyan transition duration-200"
                                    placeholder="Enter email address"
                                    disabled={isSubmitting}
                                />
                            </div>

                            {/* Phone (read-only) */}
                            {siteContactPhone && (
                                <div>
                                    <span className="block text-xs font-semibold text-gray-600 mb-1">Phone</span>
                                    <span className="block text-sm text-gray-800 py-2">{siteContactPhone}</span>
                                </div>
                            )}
                        </div>

                    </div>


                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700">Status *</label>
                        <select id="status"
                            {...register("status", { 
                                required: "Status is required.",
                                onChange: handleStatusChange 
                            })}
                            className="mt-1 block w-full input-field border-gray-300"
                            disabled={false}
                        >
                            {jobStatuses.map(status => (
                                <option key={status.value} value={status.value}>{status.label}</option>
                            ))}
                        </select>
                        {errors.status && <p className="input-error">{errors.status.message}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500">Quote Status</label>
                        <select
                            value={watch('quoteStatus') || ''}
                            onChange={handleQuoteStatusChange}
                            className="mt-1 block w-full input-field border-gray-300"
                        >
                            {quoteStatuses.map(status => (
                                <option key={status.value} value={status.value}>{status.label}</option>
                            ))}
                        </select>
                        
                        
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Noted From Franchisee</label>
                        <div className="mt-1 p-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-700 min-h-[2.5rem]">
                            {originalScheduleData?.notesFromFranchisee ? originalScheduleData.notesFromFranchisee : <span className="text-gray-400">No notes provided.</span>}
                        </div>
                    </div>
                </div>

              
                {/* Quote Status Actions Section */}
                {quoteStatus === 'quote-received' && (
                    <div className="mb-6 bg-jims-cyan-light/20 border border-jims-cyan-light rounded p-4">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex-1 mr-4">
                                <h4 className="text-sm font-semibold text-jims-cyan-dark">Quote Ready for Approval</h4>
                                {caseManagerEmail ? (
                                    <p className="text-xs text-jims-cyan-dark/80 mt-1">
                                        Quote received from franchisee. Send to Case Manager for approval.
                                    </p>
                                ) : (
                                    <p className="text-xs text-red-600 mt-1">
                                        No Case Manager email found. Please add an email address to send the quote.
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={handleSendToCaseManager}
                                disabled={sendingToCaseManager || !caseManagerEmail}
                                className={`px-4 py-2 text-sm font-medium rounded transition duration-300 disabled:cursor-not-allowed ${
                                    !caseManagerEmail
                                        ? 'bg-gray-200 text-gray-500 border border-gray-300'
                                        : 'bg-jims-cyan text-white hover:bg-jims-cyan-dark disabled:bg-gray-100 disabled:text-gray-600'
                                }`}
                            >
                                {sendingToCaseManager ? 'Sending...' : 'Send to Case Manager'}
                            </button>
                        </div>

                        {/* Notes for Case Manager */}
                        <div>
                            <label htmlFor="notesForCaseManager" className={`block text-sm font-medium mb-2 ${!caseManagerEmail ? 'text-gray-400' : 'text-jims-cyan-dark'}`}>
                                Notes for Case Manager (Optional)
                            </label>
                            <textarea
                                id="notesForCaseManager"
                                {...register("notesForCaseManager")}
                                rows="3"
                                disabled={sendingToCaseManager || !caseManagerEmail}
                                className={`w-full p-3 border rounded-md text-sm transition-colors ${
                                    errors.notesForCaseManager
                                        ? 'border-red-500'
                                        : sendingToCaseManager || !caseManagerEmail
                                            ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                            : 'border-gray-300 focus:border-jims-cyan focus:ring-1 focus:ring-jims-cyan'
                                }`}
                                placeholder={
                                    !caseManagerEmail
                                        ? "Email address required to send notes to case manager..."
                                        : "Enter any additional context or notes for the case manager during quote approval..."
                                }
                            />
                            {errors.notesForCaseManager && <p className="text-red-500 text-xs mt-1">{errors.notesForCaseManager.message}</p>}
                            <p className={`text-xs mt-1 ${!caseManagerEmail ? 'text-gray-400' : 'text-jims-cyan-dark/70'}`}>
                                {!caseManagerEmail
                                    ? "Notes will be available once a case manager email is provided."
                                    : "These notes will be included in the email sent to the case manager."
                                }
                            </p>
                        </div>
                    </div>
                )}
                {/* Pending Approval Section with Resend Option */}
                {quoteStatus === 'pending-approval' && (
                    <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded p-4">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex-1 mr-4">
                                <div className="flex items-center mb-2">
                                    <span className="inline-block w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
                                    <h4 className="text-sm font-semibold text-yellow-800">Awaiting Case Manager Approval</h4>
                                </div>
                                {caseManagerEmail ? (
                                    <p className="text-xs text-yellow-600">
                                        Quote has been sent to the Case Manager and is pending approval.
                                    </p>
                                ) : (
                                    <p className="text-xs text-red-600">
                                        No Case Manager email found. Please add an email address to resend the quote.
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={handleResendToCaseManager}
                                disabled={sendingToCaseManager || !caseManagerEmail}
                                className={`px-4 py-2 text-sm font-medium rounded transition duration-300 disabled:cursor-not-allowed ${
                                    !caseManagerEmail
                                        ? 'bg-gray-200 text-gray-500 border border-gray-300'
                                        : 'bg-yellow-500 text-white hover:bg-yellow-600 disabled:bg-gray-100 disabled:text-gray-600'
                                }`}
                            >
                                {sendingToCaseManager ? 'Resending...' : 'Resend to Case Manager'}
                            </button>
                        </div>

                        {/* Notes for Case Manager */}
                        <div>
                            <label htmlFor="notesForCaseManagerResend" className={`block text-sm font-medium mb-2 ${!caseManagerEmail ? 'text-gray-400' : 'text-yellow-800'}`}>
                                Notes for Case Manager (Optional)
                            </label>
                            <textarea
                                id="notesForCaseManagerResend"
                                {...register("notesForCaseManager")}
                                rows="3"
                                disabled={sendingToCaseManager || !caseManagerEmail}
                                className={`w-full p-3 border rounded-md text-sm transition-colors ${
                                    errors.notesForCaseManager
                                        ? 'border-red-500'
                                        : sendingToCaseManager || !caseManagerEmail
                                            ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                            : 'border-yellow-300 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500'
                                }`}
                                placeholder={
                                    !caseManagerEmail
                                        ? "Email address required to send notes to case manager..."
                                        : "Enter any additional context or notes for the case manager during quote approval..."
                                }
                            />
                            {errors.notesForCaseManager && <p className="text-red-500 text-xs mt-1">{errors.notesForCaseManager.message}</p>}
                            <p className={`text-xs mt-1 ${!caseManagerEmail ? 'text-gray-400' : 'text-yellow-700'}`}>
                                {!caseManagerEmail
                                    ? "Notes will be available once a case manager email is provided."
                                    : "These notes will be included in the email sent to the case manager."
                                }
                            </p>
                        </div>
                    </div>
                )}

                

                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700">Services *</label>
                    {serviceTypesLoading ? (
                        <p className="text-gray-500">Loading services...</p>
                    ) : allServiceTypes.length > 0 ? (
                        <div className="flex flex-wrap gap-2 w-full">
                            {allServiceTypes.map(service => {
                                const isSelected = selectedServiceIds.includes(service.id);
                                const isCarwash = isCarwashService(service.id, allServiceTypes);
                                const hasCarwashSelected = selectedServiceIds.some(id => isCarwashService(id, allServiceTypes));
                                const hasNonCarwashSelected = selectedServiceIds.some(id => !isCarwashService(id, allServiceTypes));

                                // Determine if this service should be disabled
                                const isDisabled = (isCarwash && hasNonCarwashSelected) || (!isCarwash && hasCarwashSelected);

                                return (
                                    <button
                                        key={service.id}
                                        type="button"
                                        onClick={() => !isDisabled && handleServiceToggle(service.id)}
                                        disabled={isDisabled}
                                        className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors duration-150 flex items-center justify-center ${
                                            isSelected
                                                ? 'bg-jims-cyan text-white border-jims-cyan'
                                                : isDisabled
                                                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400 cursor-pointer'
                                        }`}
                                        title={isDisabled
                                            ? (isCarwash
                                                ? 'Cannot select carwash services with other services'
                                                : 'Cannot select other services when carwash is selected')
                                            : ''
                                        }
                                    >
                                        {isSelected && <FaCheck className="mr-1 text-xs" />}
                                        {service.name}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-gray-500">No services available.</p>
                    )}
                    {serviceSelectionError && <p className="input-error mt-2">{serviceSelectionError}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end mb-6">
                    <div>
                        <label htmlFor="scheduleType" className={`block text-sm font-medium text-gray-700`}>Schedule Type *</label>
                        <select id="scheduleType"
                            {...register("scheduleType", { required: "Schedule Type is required." })}
                            className={`mt-1 block w-full input-field ${errors.scheduleType ? 'border-red-500' : 'border-gray-300'}`}
                        >
                            <option value="">Select Type...</option>
                            {scheduleTypes.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                        </select>
                        {errors.scheduleType && <p className="input-error">{errors.scheduleType.message}</p>}
                    </div>
                    <div>
                        <label htmlFor="startDate" className={`block text-sm font-medium text-gray-700`}>
                            {scheduleType === 'one-off' ? 'Date *' : 'Start Date *'}
                        </label>
                        <input id="startDate" type="date"
                            {...register("startDate", { required: "Date is required." })}
                            className={`mt-1 block w-full input-field ${errors.startDate ? 'border-red-500' : 'border-gray-300'}`}
                        />
                        {errors.startDate && <p className="input-error">{errors.startDate.message}</p>}
                    </div>
                    <div>
                        <label htmlFor="jobTime" className={`block text-sm font-medium text-gray-700`}>Job Time (Optional)</label>
                        <input id="jobTime" type="time"
                            {...register("jobTime")}
                            className={`mt-1 block w-full input-field ${errors.jobTime ? 'border-red-500' : 'border-gray-300'}`}
                        />
                    </div>
                </div>

                {/* --- Recurring Details section (no heading, radio buttons side by side) --- */}
                {scheduleType === 'recurring' && (
                    <div className="mt-6">
                        <label className={`block text-sm font-medium mb-2 text-gray-700`}>
                            Job Frequency
                        </label>
                        <div className="flex items-center gap-8 mb-4">
                            <label className="flex items-center">
                                <input
                                    id="frequencyOptionInterval"
                                    type="radio"
                                    value="interval"
                                    {...register("frequencyOption", { required: "Please select a frequency option." })}
                                    className={`h-4 w-4 border-gray-300 text-jims-cyan focus:ring-jims-cyan`}
                                />
                                <span className={`ml-2 text-gray-700`}>Every</span>
                                <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    {...register("frequencyIntervalValue", {
                                        required: frequencyOption === 'interval' && "Value is required.",
                                        valueAsNumber: true,
                                        min: { value: 1, message: "Must be at least 1." },
                                        validate: value => Number.isInteger(value) || "Must be a whole number."
                                    })}
                                    disabled={frequencyOption !== 'interval'}
                                    className={`w-16 input-field text-sm ml-2 ${errors.frequencyIntervalValue ? 'border-red-500' : 'border-gray-300'} ${frequencyOption !== 'interval' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                />
                                <select
                                    {...register("frequencyIntervalUnit", { required: frequencyOption === 'interval' && "Unit is required." })}
                                    disabled={frequencyOption !== 'interval'}
                                    className={`input-field text-sm ml-2 ${errors.frequencyIntervalUnit ? 'border-red-500' : 'border-gray-300'} ${frequencyOption !== 'interval' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                >
                                    {frequencyIntervalUnits.map(unit => (
                                        <option key={unit.value} value={unit.value}>{unit.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="flex items-center ml-6">
                                <input
                                    id="frequencyOptionMonthly"
                                    type="radio"
                                    value="monthly"
                                    {...register("frequencyOption", { required: "Please select a frequency option." })}
                                    className={`h-4 w-4 border-gray-300 text-jims-cyan focus:ring-jims-cyan`}
                                />
                                <span className={`ml-2 text-gray-700`}>Monthly</span>
                            </label>
                        </div>
                        {errors.frequencyIntervalValue && frequencyOption === 'interval' && <p className="input-error ml-7">{errors.frequencyIntervalValue.message}</p>}
                        {errors.frequencyIntervalUnit && frequencyOption === 'interval' && <p className="input-error ml-7">{errors.frequencyIntervalUnit.message}</p>}
                        {errors.frequencyOption && <p className="input-error">{errors.frequencyOption.message}</p>}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="endDate" className={`block text-sm font-medium text-gray-700`}>End Date (Optional)</label>
                                <input id="endDate" type="date"
                                    {...register("endDate")}
                                    className={`mt-1 block w-full input-field border-gray-300`}
                                />
                            </div>
                            <div>
                                <label htmlFor="occurrences" className={`block text-sm font-medium text-gray-700`}>No. of Occurrences (Optional)</label>
                                <input id="occurrences" type="number" min="1"
                                    {...register("occurrences", {
                                        valueAsNumber: true,
                                        min: { value: 1, message: "Must be at least 1." }
                                    })}
                                    className={`mt-1 block w-full input-field ${errors.occurrences ? 'border-red-500' : 'border-gray-300'}`}
                                    placeholder="e.g., 10"
                                />
                                {errors.occurrences && <p className="input-error">{errors.occurrences.message}</p>}
                            </div>
                        </div>
                    </div>
                )}

                {/* --- Allocation Type section (with dynamic required) --- */}
                <div className="space-y-6 mt-6">
                    {/* Allocation Type Pills */}
                    <div>
                        <label className={`block text-sm font-medium mb-3 text-gray-700`}>
                            Allocation Type{quoteStatus === 'pending-quote' ? '' : ' *'}
                        </label>
                        <div className="flex space-x-4">
                            {allocationTypes.map(type => (
                                <label key={type.value} className="inline-flex items-center">
                                    <input
                                        type="radio"
                                        value={type.value}
                                        {...register("allocationType", { required: quoteStatus === 'pending-quote' ? false : "Please select an allocation type." })}
                                        className="sr-only"
                                    />
                                    <div className={`px-4 py-2 rounded-full border-2 cursor-pointer transition-all ${
                                        allocationType === type.value 
                                            ? 'border-jims-cyan bg-jims-cyan text-white' 
                                            : 'border-gray-300 bg-white text-gray-700 hover:border-jims-cyan'
                                    }`}>
                                        {type.label}
                                    </div>
                                </label>
                            ))}                        </div>
                        {errors.allocationType && <p className="input-error mt-2">{errors.allocationType.message}</p>}
                    </div>

                    {/* NDIS Job Checkbox */}
                    <div>
                        <label className="inline-flex items-center">
                            <input
                                type="checkbox"
                                {...register("ndisJob")}
                                className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm font-medium text-gray-700">NDIS Job</span>
                        </label>
                    </div>

                    {/* Conditional Fields Based on Allocation Type */}
                    {allocationType && (
                        <div className="space-y-6">
                            {/* For Set Fee: Show FSE Price, Invoice Price (ex. GST), and Invoice Price */}
                            {allocationType === 'fixed-fee' && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">                                    <div>
                                        <label htmlFor="pricePerHour" className={`block text-sm font-medium text-gray-700`}>
                                            FSE Price ({ndisJob ? 'GST Free' : 'w/ GST'}){quoteStatus === 'approved' ? ' *' : ''}
                                        </label>
                                        <input id="pricePerHour" type="number" step="0.01" min="0"
                                            {...register("pricePerHour", {
                                                required: quoteStatus === 'approved' ? "FSE price per hour is required for approved jobs." : false,
                                                valueAsNumber: true,
                                                min: { value: 0, message: "Cannot be negative." }
                                            })}
                                            className={`mt-1 block w-full input-field ${errors.pricePerHour ? 'border-red-500' : 'border-gray-300'}`}
                                            placeholder="e.g., 55.00"
                                        />
                                        {errors.pricePerHour && <p className="input-error">{errors.pricePerHour.message}</p>}
                                    </div>
                                    <div>
                                        <label htmlFor="additionalPrice" className={`block text-sm font-medium text-gray-700`}>
                                            Invoice Price (ex. GST)
                                        </label>
                                        <input id="additionalPrice" type="number" step="0.01" min="0"
                                            {...register("additionalPrice", {
                                                valueAsNumber: true,
                                                min: { value: 0, message: "Cannot be negative." }
                                            })}
                                            className={`mt-1 block w-full input-field ${errors.additionalPrice ? 'border-red-500' : 'border-gray-300'}`}
                                            placeholder="e.g., 77.00"
                                        />
                                        {errors.additionalPrice && <p className="input-error">{errors.additionalPrice.message}</p>}
                                    </div>                                        <div>
                                            <label htmlFor="invoicePricePerHour" className={`block text-sm font-medium text-gray-700`}>
                                                Invoice Price ({ndisJob ? 'GST Free' : 'w/ GST'})
                                            </label>
                                        <input id="invoicePricePerHour" type="number" step="0.01" min="0"
                                            {...register("invoicePricePerHour", {
                                                valueAsNumber: true,
                                                min: { value: 0, message: "Cannot be negative." }
                                            })}
                                            className={`mt-1 block w-full input-field ${errors.invoicePricePerHour ? 'border-red-500' : 'border-gray-300'}`}
                                            placeholder="e.g., 60.50"
                                        />
                                        {errors.invoicePricePerHour && <p className="input-error">{errors.invoicePricePerHour.message}</p>}
                                    </div>
                                </div>
                            )}

                            {/* For Fixed Hours: Show allocation frequency options and fields */}
                            {allocationType === 'fixed-hours' && (
                                <div className="space-y-6">
                                    {/* Allocation Frequency Options - Only show if schedule is recurring */}
                                    {scheduleType === 'recurring' && (
                                        <fieldset className="space-y-4">
                                            <legend className={`block text-sm font-medium mb-2 text-gray-700`}>Allocation Frequency *</legend>
                                            <div className="flex items-center gap-8">
                                                <div className="flex items-center space-x-3">
                                                    <input
                                                        id="allocationOptionInterval"
                                                        type="radio"
                                                        value="interval"
                                                        {...register("allocationOption", { required: "Please select an allocation frequency option." })}
                                                        className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300"
                                                    />
                                                    <label htmlFor="allocationOptionInterval" className="flex items-center space-x-2 text-sm text-gray-700">
                                                        <span>Every</span>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            step="1"
                                                            {...register("allocationIntervalValue", {
                                                                required: allocationOption === 'interval' && "Value is required.",
                                                                valueAsNumber: true,
                                                                min: { value: 1, message: "Must be at least 1." },
                                                                validate: value => Number.isInteger(value) || "Must be a whole number."
                                                            })}
                                                            disabled={allocationOption !== 'interval'}
                                                            className={`w-16 input-field text-sm ${errors.allocationIntervalValue ? 'border-red-500' : 'border-gray-300'} ${allocationOption !== 'interval' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                        />
                                                        <select
                                                            {...register("allocationIntervalUnit", { required: allocationOption === 'interval' && "Unit is required." })}
                                                            disabled={allocationOption !== 'interval'}
                                                            className={`input-field text-sm ${errors.allocationIntervalUnit ? 'border-red-500' : 'border-gray-300'} ${allocationOption !== 'interval' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                        >
                                                            {allocationIntervalUnits.map(unit => (
                                                                <option key={unit.value} value={unit.value}>{unit.label}</option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                </div>
                                                <div className="flex items-center space-x-3">
                                                    <input
                                                        id="allocationOptionMonthly"
                                                        type="radio"
                                                        value="monthly"
                                                        {...register("allocationOption", { required: "Please select an allocation frequency option." })}
                                                        className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300"
                                                    />
                                                    <label htmlFor="allocationOptionMonthly" className={`text-sm text-gray-700`}>Monthly</label>
                                                </div>
                                            </div>
                                            {errors.allocationIntervalValue && allocationOption === 'interval' && <p className="input-error ml-7">{errors.allocationIntervalValue.message}</p>}
                                            {errors.allocationIntervalUnit && allocationOption === 'interval' && <p className="input-error ml-7">{errors.allocationIntervalUnit.message}</p>}
                                            {errors.allocationOption && <p className="input-error">{errors.allocationOption.message}</p>}
                                        </fieldset>
                                    )}

                                    {/* Fixed Hours, Invoice Price (ex. GST), FSE Price, Invoice Price */}
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                        <div>
                                            <label htmlFor="allotedHours" className={`block text-sm font-medium text-gray-700`}>
                                                Fixed Allocated Hours
                                            </label>
                                            <input id="allotedHours" type="number" step="0.01" min="0"
                                                {...register("allotedHours", {
                                                    valueAsNumber: true,
                                                    min: { value: 0, message: "Cannot be negative." }
                                                })}
                                                className={`mt-1 block w-full input-field ${errors.allotedHours ? 'border-red-500' : 'border-gray-300'}`}
                                                placeholder="e.g., 1.5"
                                            />
                                            {errors.allotedHours && <p className="input-error">{errors.allotedHours.message}</p>}
                                        </div>                                        <div>
                                            <label htmlFor="pricePerHour" className={`block text-sm font-medium text-gray-700`}>
                                                FSE price/hour ({ndisJob ? 'GST Free' : 'w/ GST'}){quoteStatus === 'approved' ? ' *' : ''}
                                            </label>
                                            <input id="pricePerHour" type="number" step="0.01" min="0"
                                                {...register("pricePerHour", {
                                                    required: quoteStatus === 'approved' ? "FSE price per hour is required for approved jobs." : false,
                                                    valueAsNumber: true,
                                                    min: { value: 0, message: "Cannot be negative." }
                                                })}
                                                className={`mt-1 block w-full input-field ${errors.pricePerHour ? 'border-red-500' : 'border-gray-300'}`}
                                                placeholder="e.g., 55.00"
                                            />
                                            {errors.pricePerHour && <p className="input-error">{errors.pricePerHour.message}</p>}
                                        </div>
                                        <div>
                                            <label htmlFor="additionalPrice" className={`block text-sm font-medium text-gray-700`}>
                                                Invoice Price (ex. GST)
                                            </label>
                                            <input id="additionalPrice" type="number" step="0.01" min="0"
                                                {...register("additionalPrice", {
                                                    valueAsNumber: true,
                                                    min: { value: 0, message: "Cannot be negative." }
                                                })}
                                                className={`mt-1 block w-full input-field ${errors.additionalPrice ? 'border-red-500' : 'border-gray-300'}`}
                                                placeholder="e.g., 77.00"
                                            />
                                            {errors.additionalPrice && <p className="input-error">{errors.additionalPrice.message}</p>}
                                        </div>
                                        <div>                                            <label htmlFor="invoicePricePerHour" className={`block text-sm font-medium text-gray-700`}>
                                                Invoice price/hour ({ndisJob ? 'GST Free' : 'w/ GST'})
                                            </label>
                                            <input id="invoicePricePerHour" type="number" step="0.01" min="0"
                                                {...register("invoicePricePerHour", {
                                                    valueAsNumber: true,
                                                    min: { value: 0, message: "Cannot be negative." }
                                                })}
                                                className={`mt-1 block w-full input-field ${errors.invoicePricePerHour ? 'border-red-500' : 'border-gray-300'}`}
                                                placeholder="e.g., 60.50"
                                            />
                                            {errors.invoicePricePerHour && <p className="input-error">{errors.invoicePricePerHour.message}</p>}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="border-t pt-6 mt-6 border-gray-200">
                    <label htmlFor="description" className={`block text-sm font-medium text-gray-700`}>Description (Invoice notes)</label>
                    <textarea id="description"
                        {...register("description")}
                        rows="3"
                        className={`mt-1 block w-full input-field ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter notes that will appear on the client invoice..."
                    />
                    {errors.description && <p className="input-error">{errors.description.message}</p>}
                </div>
                <div className="border-t pt-6 mt-6 border-gray-200">
                    <label htmlFor="adminNotes" className={`block text-sm font-medium text-gray-700`}>Admin notes</label>
                    <textarea id="adminNotes"
                        {...register("adminNotes")}
                        rows="3"
                        className={`mt-1 block w-full input-field ${errors.adminNotes ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Internal notes for admin use only..."
                    />
                    {errors.adminNotes && <p className="input-error">{errors.adminNotes.message}</p>}
                </div>
                <div>
                    <label htmlFor="notesForFranchisee" className={`block text-sm font-medium text-gray-700`}>Notes for franchisee</label>
                    <textarea id="notesForFranchisee"
                        {...register("notesForFranchisee")}
                        rows="3"
                        className={`mt-1 block w-full input-field ${errors.notesForFranchisee ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Enter any specific instructions or notes for the franchisee..."
                    />
                    {errors.notesForFranchisee && <p className="input-error">{errors.notesForFranchisee.message}</p>}
                </div>

                {/* Notes from Franchisee (read-only display) */}
                {watch('notesFromFranchisee') && (
                    <div>
                        <label className={`block text-sm font-medium text-gray-700 mb-2`}>Notes from franchisee</label>
                        <div className="mt-1 block w-full p-3 bg-gray-50 border border-gray-300 rounded-md text-gray-700">
                            {watch('notesFromFranchisee')}
                        </div>
                    </div>
                )}

               <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
                <div className="container mx-auto px-12 py-3 max-w-4xl flex justify-end gap-3"  style={{ maxWidth: '76rem' }}>
                        <Link
                            to="/job-scheduler"
                            state={location.state?.filters ? { filters: location.state.filters } : {}}
                            className="btn btn-secondary"
                        >
                            Cancel
                        </Link>
                        <button type="submit" className="btn btn-primary"
                            disabled={isSubmitting || !hasChanges || franchiseeSearch.isSearching}
                        >
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                    </div>
                </div>

                {/* Inline styles */}
                <style>{`
                    .input-field { padding: 0.5rem 0.75rem; border-width: 1px; border-radius: 0.375rem; box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); outline: none; transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out, background-color 0.2s ease-in-out; }
                    .input-field:focus { border-color: #06B6D4; box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.3); }
                    .input-field.bg-gray-100 { background-color: #f3f4f6; }
                    .input-field.cursor-wait { cursor: wait; }
                    .input-error { margin-top: 0.25rem; font-size: 0.75rem; color: #EF4444; }
                    .suggestions-list { position: absolute; z-index: 10; width: 100%; background-color: white; border: 1px solid #D1D5DB; border-radius: 0.375rem; margin-top: 0.25rem; max-height: 15rem; overflow-y: auto; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); }
                    .suggestion-item { padding: 0.5rem 0.75rem; cursor: pointer; }
                    .suggestion-item:hover { background-color: #67E8F9; }
                    .searching, .no-results { padding: 0.5rem 0.75rem; color: #6B7280; }
                    .btn { padding: 0.5rem 1rem; font-weight: 700; border-radius: 0.375rem; transition: background-color 0.3s ease, opacity 0.3s ease; border: 1px solid transparent; cursor: pointer; }
                    .btn:disabled { opacity: 0.5; cursor: not-allowed; }
                    .btn-primary { background-color: #06B6D4; color: white; }
                    .btn-primary:hover:not(:disabled) { background-color: #0E7490; }
                    .btn-secondary { background-color: #E5E7EB; color: #1F2937; }
                    .btn-secondary:hover:not(:disabled) { background-color: #D1D5DB; }
                `}</style>
            </form>

            {originalScheduleData && (
                <ActivityLog
                    entries={auditLog}
                    loading={auditLogLoading}
                    expanded={auditLogExpanded}
                    onToggle={() => setAuditLogExpanded(!auditLogExpanded)}
                    createdAt={originalScheduleData.createdAt}
                    createdLabel="Job schedule created"
                />
            )}

            {/* Add Quote Status Confirmation Modal */}
            <ConfirmationModal
                isOpen={isQuoteStatusModalOpen}
                onClose={() => {
                    setIsQuoteStatusModalOpen(false);
                    setPendingQuoteStatus(null);
                    // Reset select value
                    setValue('quoteStatus', watch('quoteStatus'));
                }}
                onConfirm={handleConfirmQuoteStatus}
                title="Confirm Quote Status Change"
                message={`Are you sure you want to change the quote status to ${formatters.formatQuoteStatus(pendingQuoteStatus)}?`}
            />

            <RegenerateJobsWarningModal
                isOpen={isRegenerateModalOpen}
                loading={regenPreviewLoading}
                preview={regenPreview}
                error={regenPreviewError}
                newStartDate={regenNewStartDate}
                submitting={isSubmitting}
                onConfirm={handleConfirmRegenerate}
                onCancel={closeRegenerateWarning}
            />

            {/* Add Status Change Confirmation Modal */}
            <ConfirmationModal
                isOpen={isStatusChangeModalOpen}
                onClose={() => {
                    setIsStatusChangeModalOpen(false);
                    setPendingSaveData(null);
                }}
                onConfirm={handleConfirmStatusChange}
                title={`Confirm Status Change to ${pendingSaveData?.status === 'inactive' ? 'Inactive' : 'Active'}`}
                message={
                    pendingSaveData?.status === 'inactive' 
                        ? "Setting this job schedule to inactive will also set all associated uncompleted jobs to inactive. Franchisees will not see these jobs anymore. Are you sure you want to continue?"
                        : "Setting this job schedule to active will make all associated jobs visible to franchisees. Any past jobs with status Quote Pending, Approved, Service Delayed, or Service On Hold will be automatically cancelled. Are you sure you want to continue?"
                }
            />

        </div>
    );
};

export default EditJobSchedulePage;
